/**
 * Geographic helpers shared across the Leaflet map components.
 */

/**
 * Split a [lat, lon] polyline into multiple segments wherever two consecutive
 * points jump across the antimeridian (180 deg longitude). Leaflet would
 * otherwise draw a single huge wrong-way segment straight across the canvas at
 * the date line. Each break inserts an interpolated point on the +/-180 edge so
 * the segments meet cleanly at the seam.
 *
 * @param {Array<[number, number]>} points - Ordered [lat, lon] waypoints.
 * @returns {Array<Array<[number, number]>>} One or more polyline segments.
 */
export const splitAtAntimeridian = (points) => {
  const segments = [];
  if (!points || points.length < 2) return segments;
  let current = [points[0]];
  for (let i = 1; i < points.length; i++) {
    const prevLon = points[i - 1][1];
    const curLon = points[i][1];
    if (Math.abs(curLon - prevLon) > 180) {
      const prevLat = points[i - 1][0];
      const curLat = points[i][0];
      const t = (180 - prevLon * Math.sign(prevLon)) / (Math.abs(prevLon) + Math.abs(curLon));
      const crossLat = prevLat + t * (curLat - prevLat);
      current.push([crossLat, prevLon > 0 ? 179.999 : -179.999]);
      segments.push(current);
      current = [[crossLat, curLon > 0 ? 179.999 : -179.999], points[i]];
    } else {
      current.push(points[i]);
    }
  }
  if (current.length > 1) segments.push(current);
  return segments;
};

/**
 * Normalize a longitude into the central world window [-180, 180). Used to
 * ANCHOR a route/track to the central world copy before it is unwrapped: a
 * live-start longitude that arrives at +200 (or -540) is folded back to its
 * canonical value so the whole voyage is drawn from the central copy and then
 * allowed to extend outward into the one neighbouring copy the ±540 canvas
 * exposes. Equivalent to unwrapLng(0, lng) but expressed as the single fold the
 * anchoring rule names.
 *
 * @param {number} lng - Any longitude (may be far outside [-180, 180]).
 * @returns {number} lng folded into [-180, 180).
 */
export const normalizeLng = (lng) => lng - 360 * Math.round(lng / 360);

/**
 * Shift a single longitude by +/-360 so it lands within 180 deg of a reference
 * longitude. Produces the "shortest longitude path" value, which may fall
 * outside [-180, 180] on purpose so a marker renders in the same voyage frame
 * as its reference (e.g. a discharge port at -79 unwrapped to +281 next to a
 * vessel at +165). No-op when the point is already within 180 deg.
 *
 * @param {number} referenceLng - The longitude to stay close to.
 * @param {number} lng - The longitude to unwrap.
 * @returns {number} lng shifted by a multiple of 360 (may exceed +/-180).
 */
export const unwrapLng = (referenceLng, lng) => {
  let out = lng;
  while (out - referenceLng > 180) out -= 360;
  while (out - referenceLng < -180) out += 360;
  return out;
};

/**
 * Unwrap a [lat, lon] point sequence into a single continuous frame. Each
 * point's longitude is shifted by +/-360 so it lies within 180 deg of the
 * PREVIOUS point (the first point anchored within 180 deg of referenceLng).
 * The result is a shortest-longitude-path continuous line whose longitudes may
 * exceed +/-180 — the correct representation for one voyage on a wrapping map,
 * where splitting at the antimeridian would tear the line into stubs. When no
 * consecutive jump exceeds 180 deg this is a no-op and returns the input coords.
 *
 * @param {number} referenceLng - Longitude the sequence start anchors to.
 * @param {Array<[number, number]>} points - Ordered [lat, lon] waypoints.
 * @returns {Array<[number, number]>} Points with continuous (unwrapped) lons.
 */
export const unwrapLngs = (referenceLng, points) => {
  if (!points || points.length === 0) return [];
  const out = [];
  let prevLng = referenceLng;
  for (let i = 0; i < points.length; i++) {
    const lat = points[i][0];
    const lng = unwrapLng(prevLng, points[i][1]);
    out.push([lat, lng]);
    prevLng = lng;
  }
  return out;
};

/**
 * Split a SECA-partitioned route into per-segment coloured polylines that share
 * ONE continuous longitude frame, so the colour breaks meet cleanly across the
 * antimeridian instead of each segment being unwrapped on its own fragile anchor.
 *
 * The server's SecaSegments RouteLines are contiguous slices of the same route:
 * consecutive segments repeat their shared boundary point, and the union densifies
 * the plain route (extra points are inserted at each SECA-zone edge, so the
 * segment stream is a superset of the route's own waypoints — it CANNOT be sliced
 * by index against them). We therefore rebuild the full route from the segments
 * themselves: concatenate them, dropping each shared boundary duplicate, unwrap
 * the whole thing ONCE (anchored to referenceLng), then slice that single
 * unwrapped array back into per-segment ranges by walking the cumulative segment
 * lengths. Every segment lands in the same frame by construction, so there is no
 * running anchor to drift and no seam to mis-join.
 *
 * Segments with fewer than 2 waypoints are skipped (they cannot form a line);
 * their boundary continuity is preserved because the next real segment still
 * shares its start with the previous real segment's end.
 *
 * @param {number} referenceLng - Longitude the whole route anchors to (the leg's
 *   live-start lon).
 * @param {Array<{waypoints: Array<[number, number]>, is_seca?: boolean}>} segments
 *   - Ordered SECA RouteLines, each with [lat, lon] waypoints.
 * @returns {Array<{latlngs: Array<[number, number]>, is_seca: boolean}>} One entry
 *   per drawable segment, its latlngs sliced from the single unwrapped frame.
 */
export const sliceUnwrappedSegments = (referenceLng, segments) => {
  if (!segments || segments.length === 0) return [];

  // Build the full continuous point stream + record each drawable segment's
  // [start, end) index range within it, dropping the boundary point shared with
  // the previous segment so it is not duplicated in the unwrapped frame.
  const full = [];
  const ranges = [];
  segments.forEach((segment) => {
    let wps = segment && segment.waypoints ? segment.waypoints : [];
    if (wps.length < 2) return;
    if (full.length > 0) {
      const prev = full[full.length - 1];
      const first = wps[0];
      if (prev[0] === first[0] && prev[1] === first[1]) wps = wps.slice(1);
    }
    const start = full.length;
    for (let i = 0; i < wps.length; i++) full.push(wps[i]);
    // The drawable slice re-includes the shared boundary point (start - 1) so the
    // coloured polyline visually touches the previous segment; the very first
    // segment has no predecessor to borrow from.
    ranges.push({ start: start > 0 ? start - 1 : 0, end: full.length, is_seca: !!(segment && segment.is_seca) });
  });

  if (ranges.length === 0) return [];
  const unwrapped = unwrapLngs(referenceLng, full);
  return ranges.map((r) => ({ latlngs: unwrapped.slice(r.start, r.end), is_seca: r.is_seca }));
};

/**
 * Longitudinal span (max minus min) of a set of longitudes. Used to guard the
 * fleet-map voyage-frame fitting: when several unwrapped voyages are unioned the
 * span can blow past a single world (360 deg), at which point the frame is worse
 * than the plain canonical fit and the caller should fall back. Returns 0 for an
 * empty/one-element input (nothing to span).
 *
 * @param {Array<number>} lngs - Longitudes (may be unwrapped, i.e. beyond +/-180).
 * @returns {number} max(lngs) - min(lngs), or 0 when fewer than two values.
 */
export const lngSpan = (lngs) => {
  if (!lngs || lngs.length < 2) return 0;
  let min = lngs[0];
  let max = lngs[0];
  for (let i = 1; i < lngs.length; i++) {
    if (lngs[i] < min) min = lngs[i];
    if (lngs[i] > max) max = lngs[i];
  }
  return max - min;
};

/**
 * Repeat a [lat, lon] polyline across the three world copies a viewer can see
 * on a wrapping/infinitely-scrollable Leaflet map at low zoom: the given coords,
 * the same coords shifted -360 deg, and shifted +360 deg in longitude. Feed it a
 * sequence that has already been unwrapped (via unwrapLngs) so the line is
 * continuous, and render one <Polyline> per returned copy — whichever world copy
 * the viewer is looking at, one of the three lands there and stays attached to
 * its vessel/ports. Latitude is untouched.
 *
 * @param {Array<[number, number]>} latlngs - Ordered [lat, lon] waypoints.
 * @returns {Array<Array<[number, number]>>} The line at [0, -360, +360] deg lon.
 */
export const worldCopies = (latlngs) => {
  if (!latlngs || latlngs.length === 0) return [];
  const shift = (delta) => latlngs.map(([lat, lng]) => [lat, lng + delta]);
  return [latlngs, shift(-360), shift(360)];
};

/**
 * Stamp a single [lat, lon] marker point into the three world copies a viewer
 * can see on the ±540 (three-world) canvas: the canonical point, and the same
 * point shifted -360 and +360 in longitude. Unlike worldCopies (which shifts a
 * whole polyline), this is for point markers — vessel arrows and port dots —
 * so each of the three copies gets its own <Marker> with a distinct React key
 * while sharing the same icon/popup. Latitude is untouched.
 *
 * @param {number} lat - Marker latitude.
 * @param {number} lon - Marker longitude (canonical [-180, 180] or unwrapped).
 * @returns {Array<[number, number]>} The point at [lon, lon-360, lon+360].
 */
export const markerCopies = (lat, lon) => [
  [lat, lon],
  [lat, lon - 360],
  [lat, lon + 360],
];
